import { createStore, get, set } from 'idb-keyval';

import { type IMediator } from '#/core/mediator';

import { SystemError } from '#/shared/exceptions/system-error';

import { SERVICES_TYPES } from '#/services-types';

import { type ILogService } from '../log';
import { type IUrlParamsService, UrlParamsName } from '../url-params';

import { type IChartStateStorageService } from './chart-state-storage.types';

export class ChartStateStorageService implements IChartStateStorageService {
  readonly #serviceName = 'ChartStateStorageService';

  readonly #store = createStore('chart-states-db', 'chart-states');

  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }

  // ? Chart state is per trading account: the same symbol must not share drawings between accounts.
  get #accountNumber() {
    return this.#urlParamsService.getParamByName(UrlParamsName.AccountNumber);
  }

  #buildKey(symbol: string, accountNumber: string): string {
    return `chart-state:${accountNumber}:${symbol}`;
  }

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');
  }

  async saveState(symbol: string, state: object): Promise<void> {
    const accountNumber = this.#accountNumber;

    if (!accountNumber) {
      this.#logService.warn(this.#serviceName, 'Missing "account-number" param in URL. Chart state is not saved.', {
        symbol,
      });

      return;
    }

    try {
      await set(this.#buildKey(symbol, accountNumber), state, this.#store);
    } catch (error) {
      this.#logService.error(
        this.#serviceName,
        error instanceof Error ? error : new SystemError(`Failed to save chart state for ${symbol}.`)
      );
    }
  }

  async loadState(symbol: string): Promise<object | null> {
    const accountNumber = this.#accountNumber;

    if (!accountNumber) {
      this.#logService.warn(this.#serviceName, 'Missing "account-number" param in URL. Chart state is not loaded.', {
        symbol,
      });

      return null;
    }

    try {
      const state = await get<object>(this.#buildKey(symbol, accountNumber), this.#store);

      return state ?? null;
    } catch (error) {
      this.#logService.error(
        this.#serviceName,
        error instanceof Error ? error : new SystemError(`Failed to load chart state for ${symbol}.`)
      );

      return null;
    }
  }
}
